import { resolveRichTextHref } from "@/lib/storyblok-link";
import type { Region } from "@/lib/region";

type RichNode = {
  type?: string;
  text?: string;
  content?: RichNode[];
  marks?: { type: string; attrs?: { href?: string; target?: string } }[];
};

export function richParagraphs(value: unknown, region: string, onDark = false): React.ReactNode[] {
  if (typeof value === "string") {
    return value
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  const inline = (node: RichNode, key: number): React.ReactNode => {
    if (node.type === "hard_break") return <br key={key} />;
    if (typeof node.text !== "string") {
      return (node.content ?? []).map(inline);
    }
    let el: React.ReactNode = node.text;
    for (const mark of node.marks ?? []) {
      if (mark.type === "bold") el = <strong className="font-semibold">{el}</strong>;
      else if (mark.type === "italic") el = <em>{el}</em>;
      else if (mark.type === "link" && mark.attrs?.href) {
        el = (
          <a
            href={resolveRichTextHref(mark.attrs.href, region as Region)}
            target={mark.attrs.target}
            rel={mark.attrs.target === "_blank" ? "noopener noreferrer" : undefined}
            className={`underline underline-offset-2 hover:opacity-80 ${
              onDark ? "text-white" : "text-teal-08"
            }`}
          >
            {el}
          </a>
        );
      }
    }
    return <span key={key}>{el}</span>;
  };

  const hasText = (n: RichNode) =>
    (n.content ?? []).some((c) => (c.text ?? "").trim() || c.content);

  const blocks: React.ReactNode[] = [];
  const walk = (nodes: RichNode[]) => {
    for (const node of nodes) {
      if (node.type === "bullet_list" || node.type === "ordered_list") {
        (node.content ?? []).forEach((item, i) => {
          const parts = (item.content ?? []).filter(hasText);
          if (parts.length === 0) return;
          const marker = node.type === "ordered_list" ? `${i + 1}. ` : "• ";
          blocks.push([
            marker,
            ...parts.map((p, j) => <span key={j}>{(p.content ?? []).map(inline)}</span>),
          ]);
        });
      } else if (node.type === "blockquote") {
        walk(node.content ?? []);
      } else if (hasText(node)) {
        const children = (node.content ?? []).map(inline);
        blocks.push(
          node.type === "heading" ? (
            <strong className="font-semibold">{children}</strong>
          ) : (
            children
          ),
        );
      }
    }
  };

  const doc = value as { content?: RichNode[] };
  walk(doc?.content ?? []);
  return blocks;
}
